import { type InfiniteData, useInfiniteQuery } from '@zg-rentals/react-query5';
import {
  type SatelliteConversationFolderTypeV2,
  type SatelliteConversationReadRepliedStatusTypeV2,
  type SatelliteConversationRenterProgressStatusTypeV2,
  SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox,
  SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox_Path,
  type SatelliteWebLandlordGetLatestConversationsForInboxRequestDto,
  type SatelliteWebLandlordGetLatestConversationsForInboxResponseDto,
  type SatelliteWebLandlordGetLatestForInboxConversationDto,
} from '@zg-rentals/ts-satellite-client';
import { useContext, useMemo } from 'react';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import { MAX_OLD_CONVERSATION_DAYS } from '../constants/ConfigConstants';
import { is4xxError } from '../utils/errorUtils';

export type InboxConversation = SatelliteWebLandlordGetLatestForInboxConversationDto;

export const INBOX_MAX_RETRIES = 2;

/**
 * Retry transient failures (5xx / network) but never a 4xx — a 403 (listing the user doesn't
 * own) / 404 (stale conversation) can't be fixed by retrying, and retrying just multiplies the
 * failure and inflates backend 4xx metrics. Exported as a pure predicate so it's unit-testable
 * without a React Query instance.
 */
export function shouldRetryInboxQuery(failureCount: number, error: unknown): boolean {
  if (is4xxError(error)) {
    return false;
  }
  return failureCount < INBOX_MAX_RETRIES;
}

export type InboxConversationsInfiniteData =
  InfiniteData<SatelliteWebLandlordGetLatestConversationsForInboxResponseDto>;

type InboxConversationsPage = SatelliteWebLandlordGetLatestConversationsForInboxResponseDto;

export const INBOX_CONVERSATIONS_TAG = 'satellite:inboxConversations';

export function buildRequestParams({
  conversationId,
  beforeConversationId,
  listingAlias,
  clientTimeZone,
  daysBack,
  folder,
  readRepliedStatus,
  renterProgressStatus,
}: {
  conversationId?: string;
  beforeConversationId?: string;
  listingAlias?: string | null;
  clientTimeZone: string;
  daysBack?: number | null;
  folder?: SatelliteConversationFolderTypeV2;
  readRepliedStatus?: SatelliteConversationReadRepliedStatusTypeV2;
  renterProgressStatus?: SatelliteConversationRenterProgressStatusTypeV2;
}): SatelliteWebLandlordGetLatestConversationsForInboxRequestDto {
  return {
    conversationId: conversationId || undefined,
    beforeConversationId: beforeConversationId || undefined,
    clientTimeZone,
    daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : undefined,
    folder: folder || undefined,
    listingAlias: listingAlias || undefined,
    readRepliedStatus: readRepliedStatus || undefined,
    renterProgressStatus: renterProgressStatus || undefined,
  };
}

export function getInboxConversationsQueryKey(params: SatelliteWebLandlordGetLatestConversationsForInboxRequestDto) {
  return [SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox_Path, params] as const;
}

type UseInboxConversationsQueryOptions = {
  conversationIdToFilterBy?: string;
  enabled?: boolean;
};

export default function useInboxConversationsQuery({
  conversationIdToFilterBy,
  enabled = true,
}: UseInboxConversationsQueryOptions = {}) {
  const { clientTimeZone, daysBack, folder, isByProperty, listingAlias, readRepliedStatus, renterProgressStatus } =
    useContext(InboxPageContext);

  const requestParams = useMemo(
    () =>
      buildRequestParams({
        conversationId: conversationIdToFilterBy || undefined,
        listingAlias: isByProperty ? listingAlias : null,
        clientTimeZone,
        daysBack,
        folder,
        readRepliedStatus,
        renterProgressStatus,
      }),
    [
      conversationIdToFilterBy,
      isByProperty,
      listingAlias,
      clientTimeZone,
      daysBack,
      folder,
      readRepliedStatus,
      renterProgressStatus,
    ],
  );

  const queryResult = useInfiniteQuery<
    InboxConversationsPage,
    unknown,
    { conversations: Array<InboxConversation>; hasNextPage: boolean },
    readonly [string, SatelliteWebLandlordGetLatestConversationsForInboxRequestDto],
    string | undefined
  >({
    queryKey: getInboxConversationsQueryKey(requestParams),
    queryFn: ({ pageParam }) =>
      SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox({
        ...requestParams,
        beforeConversationId: pageParam,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      // Same degenerate-page guard as `select`: a page can arrive with `conversations` missing, so
      // coerce before reading `.length` / the cursor entry to avoid throwing while computing the
      // next-page param on the older-conversations path.
      const conversations = lastPage.conversations ?? [];
      if (!lastPage.hasNextPage || !conversations.length) return undefined;
      return conversations[conversations.length - 1].conversationId;
    },
    select: (data) => {
      // Defend the rendered list against a degenerate page shape (missing `conversations` array,
      // or a null/undefined entry inside it). Without the guards, `flatMap` either inherits the
      // falsy value as an array slot or propagates an undefined entry, and downstream callers
      // (e.g. `conversations.map((c) => c.conversationId)` in ConversationList) crash with
      // "Cannot read properties of undefined (reading 'conversationId')".
      const conversations = data.pages
        .flatMap((page) => page.conversations ?? [])
        .filter((conversation): conversation is InboxConversation => Boolean(conversation));
      const lastPage = data.pages[data.pages.length - 1];
      return {
        conversations,
        hasNextPage: lastPage?.hasNextPage ?? false,
      };
    },
    enabled,
    // Don't retry or window-focus-refetch a 4xx (e.g. a 403 for a listing the user doesn't own);
    // doing so re-hits the endpoint repeatedly and inflates `Http403ForbiddenException`.
    retry: shouldRetryInboxQuery,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000) * (0.5 + Math.random()),
    refetchOnWindowFocus: false,
    meta: {
      tags: [INBOX_CONVERSATIONS_TAG],
      context: {
        api: 'getLatestConversationsForInbox',
        details: 'useInboxConversationsQuery - Error fetching inbox conversations',
      },
    },
  });

  return queryResult;
}
